import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  AcademicCapIcon, ArrowLeftIcon, PencilSquareIcon, XMarkIcon,
  CheckIcon, ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import api from '../../api/client';

// Settings → Onboarding Templates. Director picks which roles each
// onboarding item applies to (Phase 2 of the Employment File
// consolidation). The default is '*' (applies to everyone). Limiting
// a template to specific roles means staff in other roles never see
// it on their checklist — cooks don't see CPR, teachers don't see
// food handler card, etc.
//
// Templates with `*` in applies_to_roles override any other role
// values: an item either applies to everyone OR to a specific list.

const ROLE_LABELS = {
  director: 'Director',
  admin: 'Office Admin',
  assistant_director: 'Asst. Director',
  teacher: 'Teacher',
  float: 'Float',
  cook: 'Cook',
};

export default function OnboardingTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [filterRole, setFilterRole] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, r] = await Promise.all([
        api.get('/onboarding-templates'),
        api.get('/onboarding-templates/roles'),
      ]);
      setTemplates(t.data);
      setRoles(r.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load templates.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;
  }

  const phases = [
    { key: 'phase_1', label: 'Phase 1 — Pre-start Clearance' },
    { key: 'phase_2', label: 'Phase 2 — Onboarding' },
  ];

  // Apply role filter — when set, show only templates that include
  // this role (or '*' = applies to everyone). Lets the director ask
  // "what does a cook actually see?"
  const visibleFor = (t) => {
    if (filterRole === 'all') return true;
    return t.applies_to_roles?.includes('*') || t.applies_to_roles?.includes(filterRole);
  };

  return (
    <div className="animate-slide-up max-w-5xl mx-auto">
      <Link to="/settings" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeftIcon className="w-4 h-4" /> Back to Settings
      </Link>
      <div className="mb-6 flex items-start gap-4">
        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <ClipboardDocumentCheckIcon className="w-6 h-6 text-orange-600" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Onboarding Templates</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Control which onboarding items apply to which roles. Items marked "everyone" show on every checklist.
            Limit a template to specific roles to keep irrelevant items off a hire's plate (e.g. CPR for teachers only, food handler for cooks only).
          </p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-5 flex items-center gap-3">
        <span className="text-xs font-semibold text-gray-500 uppercase">Preview as role:</span>
        <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}
          className="text-sm rounded-lg border border-gray-200 px-3 py-1.5 bg-white">
          <option value="all">Show all templates</option>
          {roles.map(r => (
            <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>
          ))}
        </select>
        {filterRole !== 'all' && (
          <span className="text-xs text-gray-500">
            Showing items {ROLE_LABELS[filterRole] || filterRole} sees ({templates.filter(visibleFor).filter(t => t.is_active).length} active).
          </span>
        )}
      </div>

      <div className="space-y-6">
        {phases.map(phase => {
          const inPhase = templates.filter(t => t.phase === phase.key && visibleFor(t));
          return (
            <div key={phase.key}>
              <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-2">{phase.label}</h2>
              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <ul className="divide-y divide-gray-100">
                  {inPhase.length === 0 && (
                    <li className="px-5 py-6 text-sm text-gray-500 text-center">No templates in this phase.</li>
                  )}
                  {inPhase.map(t => (
                    <TemplateRow key={t.id} template={t} onEdit={() => setEditing(t)} />
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <EditModal
          template={editing}
          roles={roles}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function TemplateRow({ template, onEdit }) {
  const everyone = template.applies_to_roles?.includes('*');
  const inactive = !template.is_active;
  return (
    <li className={`px-5 py-3 flex items-start gap-3 hover:bg-gray-50/50 ${inactive ? 'opacity-60' : ''}`}>
      <div className="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0 mt-0.5">
        <AcademicCapIcon className="w-4 h-4 text-orange-500" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-gray-900">{template.step_name}</p>
          {inactive && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-gray-100 text-gray-600 border-gray-200">
              Inactive
            </span>
          )}
          {template.uploader === 'admin' && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-purple-50 text-purple-700 border-purple-200">
              Admin-handled
            </span>
          )}
        </div>
        {template.description && (
          <p className="text-xs text-gray-500 mt-0.5">{template.description}</p>
        )}
        <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
          {everyone ? (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-green-50 text-green-700 border-green-200">
              Applies to everyone
            </span>
          ) : (
            (template.applies_to_roles || []).map(r => (
              <span key={r} className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-blue-50 text-blue-700 border-blue-200">
                {ROLE_LABELS[r] || r}
              </span>
            ))
          )}
        </div>
      </div>
      <button onClick={onEdit}
        className="text-xs font-semibold text-orange-600 hover:text-orange-700 inline-flex items-center gap-1 flex-shrink-0 px-3 py-1.5 rounded-lg border border-orange-200 hover:bg-orange-50">
        <PencilSquareIcon className="w-3.5 h-3.5" /> Edit
      </button>
    </li>
  );
}

function EditModal({ template, roles, onCancel, onSaved }) {
  const wasEveryone = template.applies_to_roles?.includes('*');
  const [mode, setMode] = useState(wasEveryone ? 'everyone' : 'specific');
  const [selected, setSelected] = useState(
    new Set(wasEveryone ? [] : (template.applies_to_roles || []))
  );
  const [isActive, setIsActive] = useState(template.is_active);
  const [busy, setBusy] = useState(false);

  const toggle = (r) => {
    const s = new Set(selected);
    if (s.has(r)) s.delete(r); else s.add(r);
    setSelected(s);
  };

  const save = async () => {
    setBusy(true);
    try {
      const applies_to_roles = mode === 'everyone'
        ? ['*']
        : (selected.size > 0 ? Array.from(selected) : ['*']);
      await api.patch(`/onboarding-templates/${template.id}`, {
        applies_to_roles,
        is_active: isActive,
      });
      toast.success('Saved');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{template.step_name}</h3>
            {template.description && (
              <p className="text-xs text-gray-500 mt-0.5">{template.description}</p>
            )}
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-2">
              Who sees this item?
            </label>
            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="radio" checked={mode === 'everyone'} onChange={() => setMode('everyone')}
                  className="mt-1" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Everyone</p>
                  <p className="text-xs text-gray-500">All hires get this item on their checklist.</p>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="radio" checked={mode === 'specific'} onChange={() => setMode('specific')}
                  className="mt-1" />
                <div>
                  <p className="text-sm font-medium text-gray-900">Specific roles only</p>
                  <p className="text-xs text-gray-500">Only the selected roles see it.</p>
                </div>
              </label>
            </div>
          </div>

          {mode === 'specific' && (
            <div>
              <label className="text-[11px] font-semibold uppercase text-gray-500 block mb-2">
                Roles
              </label>
              <div className="grid grid-cols-2 gap-2">
                {roles.map(r => {
                  const on = selected.has(r);
                  return (
                    <button key={r} type="button" onClick={() => toggle(r)}
                      className={`text-left text-sm px-3 py-2 rounded-lg border flex items-center gap-2 ${
                        on ? 'border-orange-300 bg-orange-50 text-orange-800' : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}>
                      {on && <CheckIcon className="w-4 h-4 text-orange-600" />}
                      {ROLE_LABELS[r] || r}
                    </button>
                  );
                })}
              </div>
              {selected.size === 0 && (
                <p className="text-xs text-amber-700 mt-2">
                  Nothing selected — saving will fall back to "Everyone."
                </p>
              )}
            </div>
          )}

          <div className="pt-2 border-t border-gray-100">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              <span className="text-sm text-gray-700">Active (uncheck to hide from all checklists)</span>
            </label>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onCancel}
              className="text-sm px-3 py-1.5 rounded-lg text-gray-600 hover:bg-gray-100">
              Cancel
            </button>
            <button type="button" onClick={save} disabled={busy}
              className="text-sm px-4 py-1.5 rounded-lg bg-orange-500 text-white font-semibold hover:bg-orange-600 disabled:opacity-50">
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
